/**
 * The discipline options the Identity step offers a programme team.
 *
 * @module components/admin/TenantDisciplines
 */

import React, { useState } from "react";

import { DEFAULT_DISCIPLINES } from "@/components/context/TenantDisciplinesContext";
import {
  Alert,
  Button,
  type ConfirmOptions,
  ErrorAlert,
  LinkButton,
  Panel,
  PasteListDialog,
  TermList,
} from "@/components/ui";
import { Bone } from "@/components/ui/SkeletonParts";

import {
  DefaultDisciplinesPanel,
  type DefaultsState,
  type DisciplineUsage,
  DisciplineUsageNote,
} from "./DefaultDisciplinesPanel";

/** How many platform defaults an empty tenant list stands in for. */
const DEFAULT_COUNT = DEFAULT_DISCIPLINES.length;

export interface TenantDisciplinesProps {
  disciplines: readonly string[];
  /** False until the provider record has arrived; nothing can be written yet. */
  ready: boolean;
  feedback: string;
  /** Resolves to whether the list was saved. */
  onSave: (next: string[]) => Promise<boolean>;
  /** A change is being saved; another built now would drop it. */
  saving?: boolean;
  /** Why the list could not be read. The list is not drawn while it is set. */
  loadError?: string;
  onRetry?: () => void;
  /** Programmes per discipline. Absent while unknown, and no counts are drawn. */
  usage?: DisciplineUsage;
  /** Where the per-discipline report lives, linked from the tenant list. */
  reportHref?: string;
}

function defaultsState(ready: boolean, loadError: string, count: number): DefaultsState {
  if (!ready || loadError) {
    return "unknown";
  }
  return count === 0 ? "in-use" : "replaced";
}

/**
 * @example
 * ```tsx
 * <TenantDisciplines disciplines={list} ready={Boolean(record)}
 *   feedback={feedback} onSave={saveDisciplines} />
 * ```
 */
export function TenantDisciplines({
  disciplines,
  ready,
  feedback,
  onSave,
  saving = false,
  loadError = "",
  onRetry = undefined,
  usage = undefined,
  reportHref = undefined,
}: TenantDisciplinesProps): React.ReactElement {
  const [pasting, setPasting] = useState(false);
  const [failed, setFailed] = useState(false);
  const busyReason = saving ? "A change is already being saved." : undefined;
  let writeRefusal: string | undefined = busyReason;
  if (loadError) {
    writeRefusal = "The disciplines could not be loaded.";
  } else if (!ready) {
    writeRefusal = "The disciplines are still loading.";
  }

  const save = async (next: string[]): Promise<boolean> => {
    const saved = await onSave(next);
    setFailed(!saved);
    return saved;
  };

  const startFromDefaults = (): void => {
    if (writeRefusal) {
      return;
    }
    save([...DEFAULT_DISCIPLINES]).catch(() => undefined);
  };

  // Asked only for a discipline programmes hold, or the last one: emptying the
  // list hands every programme back to the platform defaults.
  const confirmRemoval = (discipline: string, count: number | undefined): ConfirmOptions | null => {
    const last = disciplines.length === 1;
    if (count === 0 && !last) {
      return null;
    }
    return {
      title: `Remove ${discipline}?`,
      body: last
        ? `This empties the list, so every programme's Identity step goes back to offering the ${DEFAULT_COUNT} platform defaults.`
        : "It is no longer offered on any programme's Identity step.",
      confirmLabel: "Remove discipline",
      tone: "danger",
      "data-testid": "discipline-remove-confirmation",
    };
  };

  let body: React.ReactNode;
  if (loadError) {
    body = (
      <ErrorAlert data-testid="tenant-disciplines-error">
        <span className="flex flex-wrap items-center gap-3">
          The disciplines could not be loaded. {loadError}
          {onRetry ? (
            <Button size="sm" variant="outline" icon="arrow-clockwise" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
        </span>
      </ErrorAlert>
    );
  } else if (!ready) {
    body = (
      <div role="status" aria-live="polite" className="grid gap-3">
        <span className="sr-only">Loading the disciplines…</span>
        {/* The note on what the list replaces, then the list's add field. */}
        <Bone className="h-12 w-full" />
        <Bone className="h-10 w-full" />
      </div>
    );
  } else {
    body = (
      <div className="grid gap-3">
        {failed && feedback ? (
          <ErrorAlert data-testid="tenant-disciplines-save-error">{feedback}</ErrorAlert>
        ) : (
          <Alert tone="info" data-testid="tenant-disciplines-source">
            {disciplines.length === 0
              ? `The Identity step offers the ${DEFAULT_COUNT} platform defaults until you add a discipline here. The first one you add replaces all of them.`
              : `The Identity step offers only the disciplines listed here. Remove them all to go back to the ${DEFAULT_COUNT} platform defaults.`}
          </Alert>
        )}
        <TermList
          terms={disciplines}
          noun="discipline"
          onChange={save}
          usage={usage}
          renderUsage={(discipline) => (
            <DisciplineUsageNote usage={usage} discipline={discipline} testIdPrefix="tenant" />
          )}
          confirmRemoval={confirmRemoval}
          disabled={Boolean(busyReason)}
          disabledReason={busyReason}
          addTestId="tenant-discipline-input"
        />
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <Panel
        headingLevel={2}
        title="Tenant disciplines"
        data-testid="tenant-disciplines"
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              icon="clipboard-text"
              onClick={() => {
                setPasting(true);
              }}
              disabled={Boolean(writeRefusal)}
              disabledReason={writeRefusal}
            >
              Paste a list
            </Button>
            {reportHref ? (
              <LinkButton to={reportHref} size="sm" variant="ghost" iconEnd="arrow-right">
                Disciplines report
              </LinkButton>
            ) : null}
          </>
        }
      >
        {body}
      </Panel>
      <DefaultDisciplinesPanel
        state={defaultsState(ready, loadError, disciplines.length)}
        usage={usage}
        startRefusal={writeRefusal}
        onStartFromDefaults={startFromDefaults}
      />
      <PasteListDialog
        open={pasting}
        onClose={() => {
          setPasting(false);
        }}
        noun="discipline"
        existing={disciplines}
        onAdd={(terms) => save([...disciplines, ...terms])}
        data-testid="tenant-disciplines-paste"
      />
    </div>
  );
}
